// Shared types and small helpers for the Agents pages.
import { Badge } from "@/components/ui/badge";

export type Agent = {
  id: number; name: string; role: string; system_prompt: string;
  provider: string | null; model: string | null; schedule: string | null; enabled: number;
  last_run_at: string | null; last_run_status: string | null;
  reports_to: number | null; department: string; goals: string; tools_json: string;
  model_tier: "light" | "standard" | "lead"; max_steps: number; autonomy: string; template_key: string | null;
};
export type AgentsResp = {
  items: Agent[]; defaultProvider: string; fallbackProvider: string | null;
  providers: Record<string, boolean>; canEdit: boolean; canRun: boolean;
  canApprove: boolean; pendingApprovals: number;
  spend?: AiSpendStatus;
};
/** Today's estimated AI spend vs the workspace's daily cap (worker/lib/aiCapPure.ts CapStatus). */
export type AiSpendStatus = {
  day: string; spentUsd: number; capUsd: number | null; capKind: "default" | "custom" | "off";
  reached: boolean; resetsAt: string; unpricedCalls: number;
};
/** GET /api/agents/status — the floating bubble's lightweight poll. */
export type AgentsStatus = { pendingApprovals: number; canRun: boolean; spend: AiSpendStatus };
export type Run = {
  id: number; agent_id: number; agent_name: string | null; shop_id: number | null; shop_name: string | null;
  trigger: string; input: string | null; output: string | null; provider: string | null; model: string | null;
  duration_ms: number | null; status: string; error: string | null; created_at: string;
};
export type RunsResp = { runs: Run[]; stats: { provider: string; model: string | null; runs: number; avg_ms: number }[] };
export type AccountLite = { id: number; name: string; company: string | null };

export type TaskSummary = {
  id: number; title: string; status: string; assigned_agent_id: number; agent_name: string | null;
  shop_id: number | null; shop_name: string | null; source: string; step_count: number;
  subtasks: number; subtasks_done: number; created_at: string; updated_at: string; finished_at: string | null;
};
export type TaskNode = {
  id: number; parent_task_id: number | null; root_task_id: number | null; assigned_agent_id: number;
  agent_name: string | null; agent_role: string | null; shop_name: string | null;
  title: string; instructions: string; status: string; result: string | null; error: string | null;
  depth: number; step_count: number; created_at: string; finished_at: string | null;
};
export type TaskEvent = {
  id: number; task_id: number; kind: string; content: string | null;
  provider: string | null; model: string | null; duration_ms: number | null; created_at: string;
};
export type TaskDetail = { rootId: number; tasks: TaskNode[]; events: TaskEvent[] };

export type Approval = {
  id: number; task_id: number; agent_id: number | null; shop_id: number | null; tool: string; risk: string;
  args_json: string; original_args_json: string | null; preview: string; status: string;
  result: string | null; error: string | null; decided_by: string | null; decision_note: string | null;
  decided_at: string | null; created_at: string; agent_name: string | null; shop_name: string | null; task_title: string | null;
};

export type ToolInfo = { name: string; description: string; args: string; risk: "read" | "internal" | "external" };

export const PROVIDER_LABEL: Record<string, string> = {
  gemini: "Gemini", ollama: "Local", anthropic: "Claude", openai: "OpenAI", grok: "Grok", kimi: "Kimi",
};
export const providerLabel = (p: string | null | undefined) => PROVIDER_LABEL[p ?? ""] ?? p ?? "—";

export const TIER_LABEL: Record<string, string> = { light: "Light", standard: "Standard", lead: "Lead" };

export const SCHEDULES: { v: string; label: string }[] = [
  { v: "none", label: "No schedule" },
  { v: "daily", label: "Every day" },
  { v: "weekly-mon", label: "Mondays" }, { v: "weekly-tue", label: "Tuesdays" }, { v: "weekly-wed", label: "Wednesdays" },
  { v: "weekly-thu", label: "Thursdays" }, { v: "weekly-fri", label: "Fridays" },
  { v: "weekly-sat", label: "Saturdays" }, { v: "weekly-sun", label: "Sundays" },
  { v: "monthly-1", label: "1st of the month" },
];
export const scheduleLabel = (s: string | null) =>
  !s ? "No schedule" : SCHEDULES.find((x) => x.v === s)?.label ?? `Cron: ${s}`;

export const fmtMs = (ms: number | null | undefined) =>
  ms == null ? "—" : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
export const fmtWhen = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "Never";

export function StatusBadge({ status }: { status: string }) {
  if (status === "ok") return <Badge variant="positive">ok</Badge>;
  if (status === "fallback") return <Badge variant="warning">fallback</Badge>;
  return <Badge variant="destructive">error</Badge>;
}

const TASK_STATUS: Record<string, { label: string; variant: "positive" | "warning" | "destructive" | "muted" | "secondary" | "outline" }> = {
  queued: { label: "Queued", variant: "muted" },
  running: { label: "Working", variant: "secondary" },
  waiting_children: { label: "Waiting on team", variant: "secondary" },
  waiting_human: { label: "Needs your answer", variant: "warning" },
  waiting_approval: { label: "Needs approval", variant: "warning" },
  done: { label: "Done", variant: "positive" },
  failed: { label: "Failed", variant: "destructive" },
  cancelled: { label: "Cancelled", variant: "outline" },
};
export const isActive = (s: string) => !["done", "failed", "cancelled"].includes(s);
export function TaskStatusBadge({ status }: { status: string }) {
  const t = TASK_STATUS[status] ?? { label: status, variant: "muted" as const };
  return <Badge variant={t.variant}>{t.label}</Badge>;
}

// Agents in org order: each boss followed by its reports, with a depth for indenting.
export function orgOrder(items: Agent[]): { agent: Agent; depth: number }[] {
  const ids = new Set(items.map((a) => a.id));
  const kids = new Map<number | null, Agent[]>();
  for (const a of items) {
    const boss = a.reports_to != null && ids.has(a.reports_to) ? a.reports_to : null;
    kids.set(boss, [...(kids.get(boss) ?? []), a]);
  }
  const out: { agent: Agent; depth: number }[] = [];
  const seen = new Set<number>();
  const walk = (boss: number | null, depth: number) => {
    for (const a of kids.get(boss) ?? []) {
      if (seen.has(a.id)) continue;
      seen.add(a.id);
      out.push({ agent: a, depth });
      walk(a.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
